import { createRouter, createWebHistory } from "vue-router";
import { nextTick } from "vue";
import {
    fullScreenLoader,
    removeFullScreenLoader,
} from "@/components/jsComponents";
import { user, maybeReauthMcp } from "@/code/user";
import { applySeo } from "@/code/seo";
import { seoForRoute } from "@/code/seo-meta";

import Main from "@/views/main.vue";
import ServiceMain from "@/views/service/main.vue";
import LandingPage from "@/views/landing.vue";
import Login from "@/views/login.vue";
import Signup from "@/views/signup.vue";
import NotFound from "@/views/not-found.vue";
import ProjectList from "@/views/project-list.vue";
import Agent from "@/views/service/agent.vue";
import Dbfile from "@/views/service/dbfile.vue";

let checkUser = async (t: any, f: any, n: any) => {
    let u = user?.user_id;
    if (u) {
        // Logged in. Entering a login-gated page: make sure the MCP session is
        // still live. If it's stale this starts the OAuth round-trip (browser
        // leaves the SPA) and we stop here without calling next(). The initial
        // navigation (f.name === undefined) is skipped because main.ts already
        // ran the pre-mount MCP check before the first paint.
        if (f?.name !== undefined) {
            const redirecting = await maybeReauthMcp(user, t.fullPath);
            if (redirecting) return; // browser leaving the SPA
        }
        return n();
    } else {
        // // subscription 페이지로 바로 접근 했는데 로그인이 안된 상태라면 로그인 페이지로 보내고, 로그인 후에 subscription 페이지로 돌아오게 함
        // if (t.name === "subscription" || t.name === "subscription-url") {
        //     let redirectPath = t.name ? t.name : "";
        //     let serviceId = t.params.service ? t.params.service : "";
        //     n(`/login?redirect=true&rdp=${redirectPath}&sid=${serviceId}`);
        //     return;
        // }
    }
    n("/login");
};

const router = createRouter({
    scrollBehavior(to, from, savedPosition) {
        // always scroll to top
        return { top: 0 };
    },
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: "/login",
            name: "login",
            component: Login,
        },
        {
            path: "/signup",
            name: "signup",
            component: Signup,
        },
        {
            path: "/signup-confirmation",
            name: "confirmation",
            component: () => import("@/views/signup-confirmation.vue"),
        },
        {
            path: "/email-verification",
            name: "verification",
            component: () => import("@/views/account-email-verification.vue"),
        },
        {
            path: "/forgot-password",
            name: "forgot",
            component: () => import("@/views/account-forgot-password.vue"),
        },
        {
            path: "/change-password",
            name: "change-password",
            component: () => import("@/views/account-change-password.vue"),
            meta: { requiresAuth: true },
            beforeEnter: checkUser,
        },
        {
            path: "/delete-account",
            name: "delete-account",
            component: () => import("@/views/account-delete.vue"),
            meta: { requiresAuth: true },
            beforeEnter: checkUser,
        },
        // {
        //     path: "/signup_confirmed_template",
        //     name: "signup_confirmed",
        //     component: () => import("@/views/email_confirm_templates/signup_confirmed_template.vue"),
        // },
        // {
        //     path: "/invitation_confirmed_template",
        //     name: "invitation_confirmed",
        //     component: () =>
        //         import("@/views/email_confirm_templates/invitation_confirmed_template.vue"),
        // },
        {
            path: "/enable-account/:email",
            name: "enable-account",
            component: () => import("@/views/account-enable.vue"),
        },
        {
            path: "/signup-success",
            name: "success",
            component: () => import("@/views/signup-success.vue"),
        },
        {
            path: "/bye",
            name: "bye",
            component: () => import("@/views/bye.vue"),
        },
        {
            // path: '/create/:name',
            path: "/create",
            name: "create",
            component: () => import("@/views/project-create.vue"),
            meta: { requiresAuth: true },
            beforeEnter: checkUser,
        },
        {
            path: "/delete-service/:id",
            name: "delete-service",
            component: () => import("@/views/project-delete.vue"),
            meta: { requiresAuth: true },
            beforeEnter: checkUser,
        },
        {
            path: "/subscription/:service",
            name: "subscription",
            component: () => import("@/views/subscription/subscription.vue"),
            meta: { requiresAuth: true },
            beforeEnter: checkUser,
        },
        {
            // pages with navbar
            path: "/",
            component: Main,
            children: [
                {
                    path: "/",
                    name: "home",
                    component: LandingPage,
                },
                {
                    path: "articles",
                    name: "articles",
                    component: () => import("@/views/articles.vue"),
                },
                {
                    path: "articles/:slug",
                    name: "article",
                    component: () => import("@/views/article.vue"),
                },
                {
                    path: "account-settings",
                    name: "account-settings",
                    component: () => import("@/views/account-settings.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: checkUser,
                },
                {
                    path: "my-projects",
                    name: "my-projects",
                    component: ProjectList,
                    meta: { requiresAuth: true },
                    beforeEnter: checkUser,
                },
                {
                    path: "my-projects/:service",
                    component: ServiceMain,
                    meta: { requiresAuth: true },
                    beforeEnter: checkUser,
                    children: [
                        {
                            path: "",
                            name: "dashboard",
                            component: () =>
                                import("@/views/service/service.vue"),
                        },
                        {
                            path: "users",
                            name: "users",
                            component: () =>
                                import("@/views/service/users/users.vue"),
                        },
                        {
                            path: "records",
                            name: "records",
                            component: () =>
                                import("@/views/service/records/records.vue"),
                        },
                        // {
                        //     path: "openid",
                        //     name: "openid",
                        //     component: () =>
                        //         import("@/views/service/openid/openid.vue"),
                        // },
                        // {
                        //     path: "mail",
                        //     name: "mail",
                        //     component: () => import("@/views/service/mail.vue"),
                        // },
                        // {
                        //     path: "newsletter",
                        //     name: "newsletter",
                        //     component: () =>
                        //         import("@/views/service/newsletter.vue"),
                        // },
                        // {
                        //     path: "hosting",
                        //     name: "hosting",
                        //     component: () =>
                        //         import("@/views/service/hosting.vue"),
                        // },
                        {
                            path: "dbfile",
                            name: "dbfile",
                            component: Dbfile,
                        },
                        // {
                        //     path: "clientsecret",
                        //     name: "clientsecret",
                        //     component: () =>
                        //         import("@/views/service/client_secret.vue"),
                        // },
                        {
                            path: "agent",
                            name: "agent",
                            component: Agent,
                        },
                    ],
                },
            ],
        },
        // 404 page
        {
            path: "/404page",
            name: "404page",
            component: NotFound,
        },
        {
            path: "/:pathMatch(.*)*",
            redirect: "/404page",
        },
    ],
});

// 깜빡임 방지용 타이머
let loadingTimer: number | null = null;

router.beforeEach(async (to, from, next) => {
    // Clear any previous timer to prevent stale loader from firing after a redirect chain
    if (loadingTimer) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
    }

    // // 너무 짧은 이동은 로딩 안 보이게 150ms 지연
    // loadingTimer = window.setTimeout(() => {
    //     fullScreenLoader();
    // }, 150);

    // Show loader only when the route path actually changes (not for query-only changes)
    if (to.path !== from.path || from.name === undefined) {
        fullScreenLoader();
    }
    next();
});

router.afterEach((to) => {
    // Rewrite title / description / canonical / social card / JSON-LD for the
    // page we just landed on. Every route shares one static head in index.html,
    // so without this the whole site claims to be `/` — see seo-meta.ts. The
    // article detail view calls applySeo() a second time once its metadata is
    // loaded, to swap in the real title and BlogPosting node.
    applySeo(seoForRoute(String(to.name ?? ""), to.path));

    // GA page-view ping, disabled with gtag.js itself (see index.html). The
    // guard already made this a no-op once the tag stopped loading; it is kept
    // commented so the pageview restores with the tag.
    // if (typeof (window as any).gtag !== "undefined") {
    //     (window as any).gtag("config", import.meta.env.VITE_GTAG, {
    //         page_path: to.fullPath,
    //     });
    // }

    if (loadingTimer) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
    }

    // Drop the full-screen overlay as soon as the destination view is in the
    // DOM. Data loading is each page's own job (section skeletons/loaders), so
    // the whole screen is never blocked on a fetch. Views still calling
    // removeFullScreenLoader() later are harmless no-ops. During an OAuth
    // return (?code=...) keep the overlay up; that flow manages it itself.
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.get("code")) {
        nextTick(() => removeFullScreenLoader());
    }
});

// 에러 발생 시에도 로딩 해제
router.onError(() => {
    if (loadingTimer) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
    }

    // Google OAuth 처리 중이 아닐 때만 로더 제거
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.get("code")) {
        removeFullScreenLoader();
    }
});

// True when the given path resolves to a route that requires login (any
// matched record — including a parent layout — carrying meta.requiresAuth).
// Used by main.ts to decide, before the first paint, whether to run the MCP
// session check. Public pages (landing, /login, /signup, ...) return false.
export function pathRequiresAuth(path: string): boolean {
    try {
        const resolved = router.resolve(path);
        return resolved.matched.some((r) => (r.meta as any)?.requiresAuth);
    } catch {
        return false;
    }
}

export default router;
